import type { PurchaseListItemInput } from '@/types/purchase-list';
import { rowToPurchaseListItem } from '@/utils/purchase-order/reportRowToPurchaseListItem';

export interface DialogVariant {
  variantId: number | null;
  variantLabel: string;
  // Raw variant value used for the suggestion-merge composite key
  // (`<productName>::<variantKeyName>`). Distinct from `variantLabel`,
  // which substitutes 'Default' for blank values purely for display.
  variantKeyName: string;
  skuLabel: string;
  // Supplier name to display under the SKU. Empty when unknown — the
  // selected report rows often lack supplier metadata, so this is filled
  // in by mergeSuggestions from the replenishment expansion.
  supplierLabel: string;
  // Nullable because the user can clear the number input. Phase 1 seeds this
  // to 1 so the dialog is submittable the moment variants render; phase 2's
  // mergeSuggestions replaces it with the real suggestion unless the user has
  // already edited it.
  quantity: number | null;
  // True after the user edits the qty input. Suggestion-merge will not
  // overwrite a quantity once the user has touched it.
  userEdited: boolean;
  // Replenishment suggestion. `null` means "not loaded yet" — the dialog
  // shows an inline spinner. A loaded value of 0 (no reorder needed) is
  // represented as 0, not null.
  suggestedQuantity: number | null;
  checked: boolean;
  row: any;
}
export interface DialogGroup {
  productName: string;
  variants: DialogVariant[];
  // True for by_product placeholder groups rendered before the variant
  // expansion returns. The template shows a per-group "Loading variants…"
  // hint while this is true with an empty variants array.
  placeholder?: boolean;
}

function asProductName(row: any): string {
  return (row?.['Item[Item Description]'] ?? '').toString();
}
function asVariantName(row: any): string {
  return (row?.variant ?? '').toString();
}
function asVariantId(row: any): number | null {
  const raw = row?.variant_id ?? row?.variantId;
  if (raw == null || raw === 'unknown') { return null; }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}
function asSuggestedQty(row: any): number | null {
  const n = Number(row?.['[inventory_replenishment]']);
  if (!Number.isFinite(n)) { return null; }
  return n;
}

// True only when every row in the input array already carries a non-null
// `[inventory_replenishment]` value. Used as a sub-check by
// `canSkipReplenishmentFetch`; on its own, suggestion coverage is not
// enough to skip the enrich step (see canSkipReplenishmentFetch comment).
//
// Empty input returns false on purpose — there's no fetch to skip, and an
// empty selection is also not a useful "full coverage" signal for callers.
export function hasFullSuggestionCoverage(rows: any[] | null | undefined): boolean {
  if (!rows || rows.length === 0) { return false; }
  for (const row of rows) {
    if (asSuggestedQty(row) === null) { return false; }
  }
  return true;
}

// True only when the dialog can render *and submit* without running the
// enrichment fetch at all. That requires every selected row to carry both:
//   1. a non-null [inventory_replenishment] (suggestion is visible immediately), AND
//   2. a finite variant_id (rowToPurchaseListItem can resolve a real id at submit).
//
// When only #1 holds (archived / inventory-not-tracked products appear on
// the report with [inventory_replenishment] populated but variant_id
// missing or 'unknown'), the strategy stays on the fast-open path but
// defers enrich to submit time via `enrichAtSubmit` so the composite-key
// fallback in mergeSuggestions can recover the id at click time.
export function canSkipReplenishmentFetch(rows: any[] | null | undefined): boolean {
  if (!hasFullSuggestionCoverage(rows)) { return false; }
  for (const row of rows as any[]) {
    if (asVariantId(row) === null) { return false; }
  }
  return true;
}

// True when at least one *checked* variant has an unresolvable variantId.
// Used by submit() to decide whether to run the lazy enrich before
// buildSubmitPayload (which would otherwise silently drop those items).
export function hasUnresolvedVariantIds(groups: DialogGroup[]): boolean {
  for (const g of groups) {
    for (const v of g.variants) {
      if (v.checked && v.variantId == null) { return true; }
    }
  }
  return false;
}

// True when at least one group is still a by_product placeholder — i.e. its
// variants have not been resolved by mergeProductExpansion yet. The dialog
// uses this to keep the Add button enabled (and its label free of a
// misleading "Add 0 items") while that expansion is in flight; submit()
// then awaits the in-flight fetch instead of the user awaiting the button.
export function hasPendingPlaceholders(groups: DialogGroup[] | null | undefined): boolean {
  for (const g of groups || []) {
    if (g.placeholder) { return true; }
  }
  return false;
}

// True when a click on Add cannot produce a payload from what is on screen,
// so the submit needs the enrichment fetch first. Two causes, both of which
// used to make the button look dead:
//   1. by_product groups still rendering as placeholders (no variants yet);
//   2. checked variants whose variant_id the selected report row never
//      carried — buildSubmitPayload drops those, so a click built an empty
//      payload and returned without a word until mergeSuggestions swapped in
//      expansion rows that do have ids.
export function needsEnrichmentBeforeSubmit(groups: DialogGroup[]): boolean {
  return hasPendingPlaceholders(groups) || hasUnresolvedVariantIds(groups);
}

// Submit-time helper: when the user clicks Add, if any checked variant
// still lacks a resolvable variantId, run the strategy's enrich+merge
// once so mergeSuggestions can replace v.row with the canonical
// expansion row (which carries a real variant_id). Returns the input
// groups unchanged on the fast path (nothing to resolve).
//
// Errors from enrich propagate to the caller so the dialog can show a
// toast and keep the dialog open for retry.
export async function resolveGroupsBeforeSubmit(
  groups: DialogGroup[],
  enrich: () => Promise<any[]>,
  mergeEnrichment: (g: DialogGroup[], r: any[]) => DialogGroup[],
): Promise<DialogGroup[]> {
  if (!hasUnresolvedVariantIds(groups)) { return groups; }
  const rows = await enrich();
  return mergeEnrichment(groups, rows);
}

function asSupplierLabel(row: any): string {
  const raw = row?.supplier ?? row?.supplierName ?? row?.supplier_name ?? row?.['Vendor Name'];
  if (typeof raw !== 'string') { return ''; }
  if (!raw || raw === '-') { return ''; }
  return raw;
}

// Used by the by_variant fast path: build dialog groups directly from the
// rows the user already selected on the report, without waiting for the
// replenishment expansion. When the selected row already carries an
// `[inventory_replenishment]` value (Report by_variant with that measure,
// BuyingTable, Supplier Products with the reorder column), the suggestion
// is rendered immediately and the qty input is prefilled — no spinner, no
// fetch needed. Rows that lack the value keep the old null/1 defaults so
// the dialog's enrichment path can still fill them in via mergeSuggestions.
export function buildInitialGroupsFromSelected(rows: any[]): DialogGroup[] {
  const order: string[] = [];
  const byProduct: Record<string, DialogGroup> = {};
  for (const row of rows || []) {
    const productName = asProductName(row);
    if (!productName) { continue; }
    const variantKeyName = asVariantName(row);
    const variantId = asVariantId(row);
    if (!byProduct[productName]) {
      byProduct[productName] = { productName, variants: [] };
      order.push(productName);
    }
    const suggested = asSuggestedQty(row);
    const quantity = (suggested != null && suggested > 0) ? suggested : 1;
    byProduct[productName].variants.push({
      variantId,
      variantLabel: variantKeyName || 'Default',
      variantKeyName,
      skuLabel: [row?.product_sku, row?.supplier_sku].filter(Boolean).join(' · '),
      supplierLabel: asSupplierLabel(row),
      quantity,
      userEdited: false,
      suggestedQuantity: suggested,
      checked: true,
      row,
    });
  }
  return order.map((p) => byProduct[p]);
}

// by_product fast path: render one group header per selected product
// immediately so the user sees something meaningful while the variant
// expansion is in flight. The variants array stays empty + placeholder=true
// until mergeProductExpansion populates it.
export function buildProductPlaceholderGroups(rows: any[]): DialogGroup[] {
  const order: string[] = [];
  const seen = new Set<string>();
  for (const row of rows || []) {
    const productName = asProductName(row);
    if (!productName) { continue; }
    if (seen.has(productName)) { continue; }
    seen.add(productName);
    order.push(productName);
  }
  return order.map((p) => ({ productName: p, variants: [], placeholder: true }));
}

// Populate by_product placeholder groups with the variants returned from
// the expansion call. Preserves placeholder ordering (insertion order from
// the user's selected rows) and clears `placeholder` so the template
// stops showing the loading hint. Products with zero matching variants
// keep an empty variants array so the template can show an empty state.
export function mergeProductExpansion(
  placeholders: DialogGroup[],
  expansionRows: any[],
): DialogGroup[] {
  const variantsByProduct = new Map<string, DialogVariant[]>();
  for (const row of expansionRows || []) {
    const variantId = asVariantId(row);
    if (variantId == null) { continue; }
    const productName = asProductName(row);
    if (!productName) { continue; }
    if (!variantsByProduct.has(productName)) {
      variantsByProduct.set(productName, []);
    }
    const suggested = asSuggestedQty(row);
    const quantity = (suggested != null && suggested > 0) ? suggested : 1;
    const variantKeyName = asVariantName(row);
    variantsByProduct.get(productName)!.push({
      variantId,
      variantLabel: variantKeyName || 'Default',
      variantKeyName,
      skuLabel: [row?.product_sku, row?.supplier_sku].filter(Boolean).join(' · '),
      supplierLabel: asSupplierLabel(row),
      quantity,
      userEdited: false,
      suggestedQuantity: suggested,
      checked: true,
      row,
    });
  }
  return placeholders.map((g) => ({
    ...g,
    placeholder: false,
    variants: variantsByProduct.get(g.productName) ?? [],
  }));
}

export function buildDialogGroups(
  rows: any[],
  opts: { dimensionCode: string; preselectedVariantIds?: Set<number> },
): DialogGroup[] {
  const order: string[] = [];
  const byProduct: Record<string, DialogGroup> = {};
  for (const row of rows || []) {
    const variantId = asVariantId(row);
    if (variantId == null) { continue; }
    const productName = asProductName(row);
    if (!productName) { continue; }
    if (!byProduct[productName]) {
      byProduct[productName] = { productName, variants: [] };
      order.push(productName);
    }
    const suggested = asSuggestedQty(row);
    const quantity = (suggested != null && suggested > 0) ? suggested : 1;
    const checked = opts.dimensionCode === 'by_variant'
      ? !!opts.preselectedVariantIds?.has(variantId)
      : true;
    const variantKeyName = asVariantName(row);
    byProduct[productName].variants.push({
      variantId,
      variantLabel: variantKeyName || 'Default',
      variantKeyName,
      skuLabel: [row?.product_sku, row?.supplier_sku].filter(Boolean).join(' · '),
      supplierLabel: asSupplierLabel(row),
      quantity,
      userEdited: false,
      suggestedQuantity: suggested,
      checked,
      row,
    });
  }
  return order.map((p) => byProduct[p]);
}

// Merge replenishment suggestions into existing dialog groups built from the
// selected report rows. Matches first by variant_id (when both sides have one),
// falling back to a `<productName>::<variantKeyName>` composite key. Replaces
// the qty input with the suggested value only when the user hasn't already
// edited it. Always replaces the underlying `row` so subsequent submit picks
// up enriched fields (supplier/cost/etc.) from the replenishment response.
export function mergeSuggestions(groups: DialogGroup[], expansionRows: any[]): DialogGroup[] {
  const byVariantId = new Map<number, { qty: number | null; row: any }>();
  const byNameKey = new Map<string, { qty: number | null; row: any }>();
  for (const r of expansionRows || []) {
    const id = asVariantId(r);
    const productName = asProductName(r);
    const variantName = asVariantName(r);
    const qty = asSuggestedQty(r);
    const entry = { qty, row: r };
    if (id != null) { byVariantId.set(id, entry); }
    byNameKey.set(`${productName}::${variantName}`, entry);
  }
  return groups.map((g) => ({
    ...g,
    variants: g.variants.map((v) => {
      const found = (v.variantId != null && byVariantId.has(v.variantId))
        ? byVariantId.get(v.variantId)
        : byNameKey.get(`${g.productName}::${v.variantKeyName}`);
      if (!found) { return v; }
      const enrichedSupplier = asSupplierLabel(found.row);
      const next: DialogVariant = {
        ...v,
        suggestedQuantity: found.qty,
        row: found.row,
        // Prefer the expansion row's supplier when present; fall back to
        // whatever the initial row had.
        supplierLabel: enrichedSupplier || v.supplierLabel,
      };
      if (!v.userEdited && found.qty != null && found.qty > 0) {
        next.quantity = found.qty;
      }
      return next;
    }),
  }));
}

export function buildSubmitPayload(groups: DialogGroup[], suppliers: any[] = []): PurchaseListItemInput[] {
  const out: PurchaseListItemInput[] = [];
  for (const g of groups) {
    for (const v of g.variants) {
      if (!v.checked) { continue; }
      // quantity is `number | null` — null means the user never typed one
      // (phase 1 leaves the box blank). Bind to a local so TS narrows it;
      // Number.isFinite() alone does not.
      const qty = v.quantity;
      if (qty == null || !Number.isFinite(qty) || qty <= 0) { continue; }
      const base = rowToPurchaseListItem(v.row, suppliers);
      if (base.variantId == null) { continue; }
      out.push({ ...base, quantity: qty });
    }
  }
  return out;
}

// AS-10567 phase 2: start the reorder-suggestion fetch and return its
// promise WITHOUT awaiting it. The caller (refresh()) deliberately drops the
// handle so the dialog stays interactive — the user types quantities and
// submits against the variants phase 1 already rendered, and the Suggested
// column fills in behind them if and when this lands.
//
// Returns null for single-phase strategies (by_variant, supplier-products),
// which leave `enrichSuggestions` undefined and keep their old behavior.
//
// A phase-2 failure is swallowed on purpose: the suggestion is an
// enhancement, not a requirement. The variants are already usable, so
// surfacing an error here (or clearing the groups) would take away a working
// dialog over a missing convenience.
export function startSuggestionPhase(
  strategy: AddToPurchaseListStrategy,
  getGroups: () => DialogGroup[],
  setGroups: (groups: DialogGroup[]) => void,
  isCurrent: () => boolean,
  setLoadingSuggestions: (loading: boolean) => void,
): Promise<void> | null {
  const enrichSuggestions = strategy?.enrichSuggestions;
  const merge = strategy?.mergeSuggestionEnrichment;
  if (!enrichSuggestions || !merge) { return null; }

  setLoadingSuggestions(true);
  return (async () => {
    try {
      const rows = await enrichSuggestions();
      // Dialog was reopened (or closed) while this was in flight — applying
      // it now would splice rows from a previous selection into the current
      // groups.
      if (!isCurrent()) { return; }
      setGroups(merge(getGroups(), rows));
    } catch {
      // Intentionally silent — see the note above.
    } finally {
      if (isCurrent()) { setLoadingSuggestions(false); }
    }
  })();
}

// AS-10567 phase 1 — variant identity. Mirrors `startSuggestionPhase`, but the
// caller DOES await this one: by_product has nothing but placeholder groups
// until it lands.
//
// Takes the in-flight `rows` promise rather than calling strategy.enrich()
// itself, so refresh() can hand that SAME promise to a submit that fires
// before it lands — one fetch, two consumers.
//
// Resolves true only when the merge actually landed on the current refresh;
// false when the fetch failed or a newer refresh() superseded this one (the
// caller uses that to decide whether to chain phase 2).
export function startEnrichPhase(
  strategy: AddToPurchaseListStrategy,
  rows: Promise<any[]>,
  getGroups: () => DialogGroup[],
  setGroups: (groups: DialogGroup[]) => void,
  isCurrent: () => boolean,
  setLoadingSuggestions: (loading: boolean) => void,
  setErrorMessage: (message: string) => void,
): Promise<boolean> {
  setLoadingSuggestions(true);
  return (async () => {
    try {
      const resolved = await rows;
      // Dialog was reopened (or closed) while this was in flight — see the
      // same guard in startSuggestionPhase.
      if (!isCurrent()) { return false; }
      if (strategy.mergeEnrichment) {
        setGroups(strategy.mergeEnrichment(getGroups(), resolved));
      }
      return true;
    } catch {
      if (!isCurrent()) { return false; }
      if (strategy.enrichmentRequired) {
        setErrorMessage('Could not load variants. Please try again.');
      }
      // otherwise silently keep initialGroups — user can still submit.
      setLoadingSuggestions(false);
      return false;
    }
  })();
}

// Background-submit resolver (AS-10567 follow-up). The user clicked Add while
// the by_product groups were still placeholders and asked NOT to wait for the
// expansion, so the dialog closes on the click and this finishes behind them:
// it awaits the fetch refresh() already started — no second call — merges it
// into the groups as they were at click time, and returns the payload.
//
// Takes the groups by value on purpose: the dialog is closed by now and may
// have been reopened against a different selection, so reading component
// state here would submit the wrong products.
export async function resolvePendingSubmitPayload(
  groups: DialogGroup[],
  rows: Promise<any[]>,
  mergeEnrichment: ((g: DialogGroup[], r: any[]) => DialogGroup[]) | undefined,
  suppliers: any[] = [],
): Promise<PurchaseListItemInput[]> {
  const resolved = await rows;
  const merged = mergeEnrichment ? mergeEnrichment(groups, resolved) : groups;
  return buildSubmitPayload(merged, suppliers);
}

// Whether the Add button is clickable. Placeholder groups count as submittable
// even though they contribute 0 to `totalCheckedWithQty`: the variants they
// will resolve into are pre-checked with a qty, and submit() awaits that
// resolution itself (AS-10567). Gating on the count alone is what used to lock
// the user out of Add for the whole by_product expansion.
export function computeCanSubmit(state: {
  loading: boolean;
  submitting: boolean;
  errorMessage: string;
  totalCheckedWithQty: number;
  groups: DialogGroup[];
}): boolean {
  if (state.loading || state.submitting || state.errorMessage) { return false; }
  return state.totalCheckedWithQty > 0 || hasPendingPlaceholders(state.groups);
}

// Label for the Add button. While placeholders are pending the real count is
// unknowable, and "Add 0 items" on an enabled button reads like a no-op.
export function computeSubmitLabel(totalCheckedWithQty: number, groups: DialogGroup[]): string {
  if (totalCheckedWithQty > 0) { return `Add ${totalCheckedWithQty} items`; }
  return hasPendingPlaceholders(groups) ? 'Add items' : 'Add 0 items';
}

export interface AddToPurchaseListStrategy {
  // Rendered synchronously when the dialog opens.
  initialGroups: DialogGroup[];
  // Optional background enrichment — typically a replenishment-suggestion fetch.
  enrich?: () => Promise<any[]>;
  // Merge step for enrichment rows. Paired with `enrich`.
  mergeEnrichment?: (groups: DialogGroup[], rows: any[]) => DialogGroup[];
  // true → enrichment failure renders inline error + Retry (by_product needs variants).
  // false → enrichment failure silently keeps initialGroups (by_variant / supplier-products
  //         already have usable rows).
  enrichmentRequired?: boolean;
  // When true, the dialog does NOT auto-run enrich() at open time. Instead,
  // submit() runs it lazily — only if any checked variant still has an
  // unresolved variant_id. Used when initial rows already carry every
  // suggestion the dialog needs to render, so the open-time fetch can be
  // skipped entirely on the happy path. The strategy still provides
  // enrich+mergeEnrichment so submit() can recover variant_ids for
  // archived/inventory-not-tracked rows when the user actually clicks Add.
  enrichAtSubmit?: boolean;
  // AS-10567 phase 2. When present, the dialog fires this AFTER `enrich`
  // resolves and does NOT wait for it: `enrich` brings back variant identity
  // (fast), this brings back the reorder suggestions (slow, forecast cube).
  // The user can type quantities and submit while it is still in flight.
  //
  // Optional so single-phase strategies (by_variant, supplier-products) are
  // unaffected — they leave both undefined and keep their existing behavior.
  enrichSuggestions?: () => Promise<any[]>;
  // Merge step for phase-2 rows. Paired with `enrichSuggestions`.
  mergeSuggestionEnrichment?: (groups: DialogGroup[], rows: any[]) => DialogGroup[];
}

export type AddToPurchaseListStrategyBuilder = () => AddToPurchaseListStrategy;
